import React, { useEffect } from 'react';
import { Command } from 'cmdk';
import {
  BarChart3,
  CreditCard,
  Users,
  Building2,
  Activity,
  Home,
  Send,
  MessageSquare,
  Mail,
  RefreshCcw,
  ExternalLink,
  ArrowRight,
  Coins,
  UserCog,
  KeyRound,
  ShieldOff,
  Sliders,
  UserPlus,
  Trash2,
  GitMerge,
} from 'lucide-react';
import type { AdminTabId } from './types';

/**
 * Admin-scoped ⌘K command palette.
 *
 * Built on `cmdk` (industry standard, shadcn pattern). Indexes every admin
 * tab + every admin action endpoint as a command. Visual cue ported from
 * Nick Velten's vanilla admin reference (basgosewisch/stagery-ai @ 7e6e63b)
 * — overlay backdrop blur, bordered modal, uppercase group labels, kbd footer.
 *
 * Action commands trigger callbacks the parent supplies; if the action needs
 * a target (user id, code id), the parent opens its own picker after the
 * palette closes.
 */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (tab: AdminTabId) => void;
  onAction: (id: ActionId) => void;
}

export type ActionId =
  | 'refresh'
  | 'open-vercel'
  | 'open-sentry'
  | 'grant-credits'
  | 'set-role'
  | 'impersonate'
  | 'invite-user'
  | 'merge-duplicate'
  | 'bulk-delete'
  | 'create-code'
  | 'reset-preferences'
  | 'revoke-code';

const TAB_ITEMS: { id: AdminTabId; label: string; sub: string; icon: React.ComponentType<{ size?: number }> }[] = [
  { id: 'overview', label: 'Overzicht', sub: 'KPI-dashboard', icon: BarChart3 },
  { id: 'revenue', label: 'Inkomsten', sub: 'Omzet en betalingen', icon: CreditCard },
  { id: 'users', label: 'Gebruikers', sub: 'Alle accounts', icon: Users },
  { id: 'kantoren', label: 'Kantoren', sub: 'Activiteit per kantoor', icon: Building2 },
  { id: 'properties', label: 'Woningen', sub: 'Alle properties', icon: Home },
  { id: 'renders', label: 'Renders', sub: 'Output + gebruik', icon: Activity },
  { id: 'invites', label: 'Uitnodigingen', sub: 'Codes, invites, referrals', icon: Send },
  { id: 'feedback', label: 'Feedback', sub: 'User + correction feedback', icon: MessageSquare },
  { id: 'preferences', label: 'Voorkeuren', sub: 'Globale + per-user', icon: Sliders },
  { id: 'logs', label: 'Logs', sub: 'Email + audit trail', icon: Mail },
];

const ACTION_ITEMS: {
  id: ActionId;
  label: string;
  sub: string;
  icon: React.ComponentType<{ size?: number }>;
  destructive?: boolean;
}[] = [
  { id: 'grant-credits', label: 'Credits toekennen…', sub: 'Bulk credits aan geselecteerde gebruikers', icon: Coins },
  { id: 'set-role', label: 'Rol instellen…', sub: 'medewerker / beheerder / superadmin', icon: UserCog },
  { id: 'impersonate', label: 'Inloggen als gebruiker…', sub: '1u JWT (audit-logged)', icon: KeyRound },
  { id: 'invite-user', label: 'Gebruiker uitnodigen…', sub: 'Stuur admin invite', icon: UserPlus },
  { id: 'create-code', label: 'Toegangscode aanmaken…', sub: 'Nieuwe invite/promo code', icon: KeyRound },
  { id: 'revoke-code', label: 'Toegangscode intrekken…', sub: 'Maak code ongeldig', icon: ShieldOff, destructive: true },
  { id: 'reset-preferences', label: 'Voorkeuren resetten…', sub: 'Wis staging_preferences (audit-logged)', icon: Sliders, destructive: true },
  { id: 'merge-duplicate', label: 'Duplicate user samenvoegen…', sub: 'Re-parent properties + renders', icon: GitMerge },
  { id: 'bulk-delete', label: 'Gebruikers verwijderen…', sub: 'Soft-delete geselecteerde users', icon: Trash2, destructive: true },
  { id: 'refresh', label: 'Vernieuw alle data', sub: 'Herlaad stats, users, payments', icon: RefreshCcw },
  { id: 'open-vercel', label: 'Open Vercel', sub: 'Deploys + runtime logs', icon: ExternalLink },
  { id: 'open-sentry', label: 'Open Sentry', sub: 'Recente errors + traces', icon: ExternalLink },
];

const groupHeading =
  '[&_[cmdk-group-heading]]:px-5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.12em] [&_[cmdk-group-heading]]:text-ink/55 [&_[cmdk-group-heading]]:dark:text-white/55';

const itemBase =
  'grid grid-cols-[26px_1fr_auto] items-center gap-2.5 px-5 py-2.5 text-[13px] cursor-pointer aria-selected:bg-accent/[0.08] dark:aria-selected:bg-accent/15 hover:bg-black/[0.03] dark:hover:bg-white/5';

const iconBase =
  'w-6 h-6 rounded-md grid place-items-center bg-black/5 dark:bg-white/10 text-ink/60 dark:text-white/70 group-aria-selected:bg-accent group-aria-selected:text-white';

export const AdminCommandPalette: React.FC<Props> = ({ open, onOpenChange, onNavigate, onAction }) => {
  // Global ⌘K / Ctrl+K toggle.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  const select = (run: () => void) => {
    onOpenChange(false);
    setTimeout(run, 30);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[600] flex items-start justify-center pt-[14vh] px-5 bg-ink/50 dark:bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}
      role="presentation"
    >
      <Command
        label="Beheerconsole snelzoeker"
        className="w-full max-w-[620px] bg-white dark:bg-ink rounded-2xl border border-black/8 dark:border-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.35)] overflow-hidden"
        loop
      >
        <div className="flex items-center gap-3 px-5 border-b border-black/8 dark:border-white/10">
          <Command.Input
            autoFocus
            placeholder="Zoek tabs of acties…"
            className="flex-1 py-[18px] text-[15px] font-sans outline-none bg-transparent text-ink dark:text-white placeholder:text-ink/40 dark:placeholder:text-white/40"
          />
        </div>

        <Command.List className="max-h-[360px] overflow-y-auto py-1.5">
          <Command.Empty className="py-10 text-center text-sm text-ink/55 dark:text-white/55">
            Geen resultaten
          </Command.Empty>

          <Command.Group heading="Tabs" className={groupHeading}>
            {TAB_ITEMS.map((t) => {
              const Icon = t.icon;
              return (
                <Command.Item
                  key={t.id}
                  value={`tab ${t.label} ${t.sub}`}
                  onSelect={() => select(() => onNavigate(t.id))}
                  className={`group ${itemBase}`}
                >
                  <span className={iconBase}><Icon size={14} /></span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-ink dark:text-white truncate tracking-[-0.005em]">{t.label}</span>
                    <span className="block text-[11px] text-ink/55 dark:text-white/55 truncate mt-[1px]">{t.sub}</span>
                  </span>
                  <ArrowRight size={12} className="text-ink/35 dark:text-white/35" />
                </Command.Item>
              );
            })}
          </Command.Group>

          <Command.Group heading="Acties" className={groupHeading}>
            {ACTION_ITEMS.map((a) => {
              const Icon = a.icon;
              return (
                <Command.Item
                  key={a.id}
                  value={`actie ${a.label} ${a.sub}`}
                  onSelect={() => select(() => onAction(a.id))}
                  className={`group ${itemBase}`}
                >
                  <span className={`${iconBase} ${a.destructive ? 'group-aria-selected:bg-red-600' : ''}`}>
                    <Icon size={14} />
                  </span>
                  <span className="min-w-0">
                    <span className={`block font-semibold truncate tracking-[-0.005em] ${a.destructive ? 'text-red-700 dark:text-red-300' : 'text-ink dark:text-white'}`}>{a.label}</span>
                    <span className="block text-[11px] text-ink/55 dark:text-white/55 truncate mt-[1px]">{a.sub}</span>
                  </span>
                  <ArrowRight size={12} className="text-ink/35 dark:text-white/35" />
                </Command.Item>
              );
            })}
          </Command.Group>
        </Command.List>

        <div className="flex items-center justify-between px-5 py-2 border-t border-black/8 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] text-[10.5px] font-medium text-ink/55 dark:text-white/55">
          <span className="flex items-center gap-1.5">
            <kbd className="bg-white dark:bg-ink/60 border border-black/8 dark:border-white/10 rounded px-1.5 py-[1px] font-sans text-[10px] font-semibold">↑↓</kbd> navigeer
            <span className="mx-1">·</span>
            <kbd className="bg-white dark:bg-ink/60 border border-black/8 dark:border-white/10 rounded px-1.5 py-[1px] font-sans text-[10px] font-semibold">↵</kbd> open
            <span className="mx-1">·</span>
            <kbd className="bg-white dark:bg-ink/60 border border-black/8 dark:border-white/10 rounded px-1.5 py-[1px] font-sans text-[10px] font-semibold">esc</kbd> sluit
          </span>
          <span className="flex items-center gap-1">
            <kbd className="bg-white dark:bg-ink/60 border border-black/8 dark:border-white/10 rounded px-1.5 py-[1px] font-sans text-[10px] font-semibold">⌘</kbd>
            <kbd className="bg-white dark:bg-ink/60 border border-black/8 dark:border-white/10 rounded px-1.5 py-[1px] font-sans text-[10px] font-semibold">K</kbd>
          </span>
        </div>
      </Command>
    </div>
  );
};
